import React, { useEffect, useState, useRef } from 'react';
import { QuoteForm } from './QuoteForm';
import Hls from 'hls.js';
import { ArrowRightIcon, CheckCircleIcon } from 'lucide-react';
export const HeroSection = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoError, setVideoError] = useState(false);
  const [step, setStep] = useState(1);
  useEffect(() => {
    // Function to set up HLS and attempt video playback
    const setupVideo = async () => {
      if (!videoRef.current) return;
      const videoElement = videoRef.current;
      const hlsUrl = 'https://vz-f38c8037-d0c.b-cdn.net/c33f8b68-781a-493b-8814-6c299a844396/playlist.m3u8';
      const mp4Url = 'https://player.vimeo.com/external/429965591.sd.mp4?s=2c3f0e5fb6c9d3df4e5b7909bee51af13dfaa8e5&profile_id=164&oauth2_token_id=57447761';
      // Function to attempt playing the video
      const attemptPlay = async () => {
        try {
          await videoElement.play();
        } catch (error) {
          console.error('Video playback failed:', error);
          // If autoplay fails, try again after user interaction
          const playOnInteraction = () => {
            videoElement.play().then(() => {
              document.removeEventListener('click', playOnInteraction);
              document.removeEventListener('touchstart', playOnInteraction);
            }).catch(err => console.error('Still failed to play after interaction:', err));
          };
          document.addEventListener('click', playOnInteraction);
          document.addEventListener('touchstart', playOnInteraction);
        }
      };
      // First try using HLS.js if supported
      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          backBufferLength: 90
        });
        hls.loadSource(hlsUrl);
        hls.attachMedia(videoElement);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          attemptPlay();
        });
        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            hls.destroy();
            videoElement.src = mp4Url;
            attemptPlay();
          }
        });
      }
      // For Safari and iOS which have native HLS support
      else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
        videoElement.src = hlsUrl;
        attemptPlay();
      }
      // Fallback to MP4
      else {
        videoElement.src = mp4Url;
        attemptPlay();
      }
      videoElement.addEventListener('error', () => {
        setVideoError(true);
      });
    };
    setupVideo();
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.removeAttribute('src');
        videoRef.current.load();
      }
      document.removeEventListener('click', () => {});
      document.removeEventListener('touchstart', () => {});
    };
  }, []);
  const handleNextStep = () => {
    setStep(2);
  };
  return <section className="relative w-full min-h-[100svh] overflow-hidden" data-id="element-61">
      {/* Background video container with more visible overlay */}
      <div className="absolute inset-0 z-0" data-id="element-62">
        <video ref={videoRef} autoPlay loop muted playsInline className="absolute inset-0 min-w-full min-h-full object-cover" poster="https://images.unsplash.com/photo-1540946485063-a40da27545f8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80" data-id="element-63">
          Your browser does not support the video tag.
        </video>
        {/* Reduced opacity overlay to make video more visible */}
        <div className="absolute inset-0 bg-black opacity-50 z-10" data-id="element-64"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/60 z-10" data-id="element-65"></div>
      </div>
      {/* Content with desktop two-column layout */}
      <div className="relative z-20 container mx-auto px-4 flex flex-col items-center justify-center min-h-[100svh] py-8" data-id="element-66">
        {step === 1 ? <div className="w-full md:flex md:flex-row md:items-center md:justify-between md:gap-8" data-id="element-67">
            {/* Left column for desktop */}
            <div className="md:w-1/2 w-full max-w-md mx-auto md:mx-0" data-id="element-68">
              {/* Logo */}
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/1XgL9PHWTpLQw2LSGP2EPT/marco-leer_Logo.png" alt="Marco Leer" className="w-28 mb-6 mx-auto md:mx-0 md:w-32" data-id="element-69" />
              {/* Headline */}
              <h1 className="text-4xl font-bold mb-4 text-white text-center md:text-left md:text-6xl" data-id="element-70">
                <span className="text-[#234837] bg-white px-2 inline-block mb-2" data-id="element-71">
                  Vakmanschap
                </span>{' '}
                <br className="hidden md:block" data-id="element-72" />
                <span className="inline-block" data-id="element-73">in Bootstoffering</span>
              </h1>
              {/* Key benefits */}
              <div className="space-y-3 mb-8 mt-6" data-id="element-74">
                <div className="flex items-center" data-id="element-75">
                  <CheckCircleIcon className="w-5 h-5 text-white mr-2 flex-shrink-0" data-id="element-76" />
                  <p className="text-white text-base" data-id="element-77">
                    <span className="font-bold" data-id="element-78">35+ jaar ervaring</span> op het
                    water
                  </p>
                </div>
                <div className="flex items-center" data-id="element-79">
                  <CheckCircleIcon className="w-5 h-5 text-white mr-2 flex-shrink-0" data-id="element-80" />
                  <p className="text-white text-base" data-id="element-81">
                    <span className="font-bold" data-id="element-82">
                      Waterbestendige materialen
                    </span>{' '}
                    van topkwaliteit
                  </p>
                </div>
                <div className="flex items-center" data-id="element-83">
                  <CheckCircleIcon className="w-5 h-5 text-white mr-2 flex-shrink-0" data-id="element-84" />
                  <p className="text-white text-base" data-id="element-85">
                    <span className="font-bold" data-id="element-86">2 jaar garantie</span> op al ons
                    vakwerk
                  </p>
                </div>
              </div>
            </div>
            {/* Right column for desktop */}
            <div className="md:w-1/2 w-full max-w-md mx-auto" data-id="element-87">
              {/* Image and CTA together with no spacing */}
              <div className="w-full flex flex-col items-center" data-id="element-88">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/i3gaJggHvEGgEXqCzjrUef/Ontvang_binnen_1_werkdag_een_prijsindicatie_op_maat_%281%29.png" alt="Ontvang binnen 1 werkdag een prijsindicatie op maat" className="w-full max-w-[280px]" data-id="element-89" />
                <button onClick={handleNextStep} className="w-full bg-[#234837] hover:bg-[#1a3629] text-white font-bold py-4 px-4 rounded-md flex items-center justify-center transition-colors text-lg -mt-1" data-id="element-90">
                  <span data-id="element-91">VRAAG VRIJBLIJVEND OFFERTE AAN</span>
                  <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-92" />
                </button>
                <p className="text-center text-sm mt-3 text-gray-300" data-id="element-93">
                  Binnen 24 uur persoonlijk contact - Geen verplichtingen
                </p>
              </div>
            </div>
          </div> : <div className="w-full max-w-md mx-auto md:max-w-4xl md:flex md:items-center md:gap-8" data-id="element-94">
            {/* Left column for form step on desktop */}
            <div className="md:w-1/2 mb-8 md:mb-0 hidden md:block" data-id="element-95">
              <h1 className="text-4xl font-bold mb-6 text-white md:text-6xl" data-id="element-96">
                <span className="text-[#234837] bg-white px-2 inline-block mb-2" data-id="element-97">
                  Vakmanschap
                </span>{' '}
                <br data-id="element-98" />
                <span className="inline-block" data-id="element-99">in Bootstoffering</span>
              </h1>
              <div className="space-y-3 mt-6" data-id="element-100">
                <div className="flex items-center" data-id="element-101">
                  <CheckCircleIcon className="w-5 h-5 text-white mr-2 flex-shrink-0" data-id="element-102" />
                  <p className="text-white text-base" data-id="element-103">
                    <span className="font-bold" data-id="element-104">35+ jaar ervaring</span> op het
                    water
                  </p>
                </div>
                <div className="flex items-center" data-id="element-105">
                  <CheckCircleIcon className="w-5 h-5 text-white mr-2 flex-shrink-0" data-id="element-106" />
                  <p className="text-white text-base" data-id="element-107">
                    <span className="font-bold" data-id="element-108">Binnen 24 uur</span> persoonlijk
                    contact
                  </p>
                </div>
              </div>
            </div>
            {/* Right column for form on desktop */}
            <div className="md:w-1/2" data-id="element-109">
              {/* Step 2: Form */}
              <div className="bg-white rounded-lg shadow-xl p-6 w-full" data-id="element-110">
                <button onClick={() => setStep(1)} className="text-[#234837] font-medium mb-4 flex items-center" data-id="element-111">
                  <ArrowRightIcon className="w-4 h-4 mr-1 transform rotate-180" data-id="element-112" />
                  Terug
                </button>
                <h2 className="text-2xl font-bold mb-6 text-center" data-id="element-113">
                  Ontvang binnen 1 werkdag uw{' '}
                  <span className="text-[#234837]" data-id="element-114">persoonlijke offerte</span>
                </h2>
                <QuoteForm className="space-y-4" data-id="element-115" />
              </div>
            </div>
          </div>}
      </div>
    </section>;
};